import { useBreeds, useCategories, useLots, useUpdateAnimal } from '@/api/hooks';
import { AnimalCoverPhotoPicker } from '@/components/animals/AnimalCoverPhotoPicker';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { WeightField } from '@/components/ui/WeightDialPicker';
import { getErrorMessage } from '@/lib/api';
import { breedLabel } from '@/lib/format';
import type { Animal } from '@/types/api';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

type AnimalFichaFormProps = {
  animal: Animal;
};

function animalToForm(animal: Animal) {
  return {
    internal_number: animal.internal_number,
    name: animal.name ?? '',
    lot_id: animal.lot_id ? String(animal.lot_id) : '',
    category_code: animal.category_code,
    breed_id: animal.breed_id ? String(animal.breed_id) : '',
    birth_date: animal.birth_date ?? '',
    weaning_date: animal.weaning_date ?? '',
    current_weight_kg: animal.current_weight_kg != null ? String(animal.current_weight_kg) : '',
    chip_number: animal.chip_number ?? '',
    registry_code: animal.registry_code ?? '',
    notes: animal.notes ?? '',
  };
}

function Field({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={className}>{children}</div>;
}

export function AnimalFichaForm({ animal }: AnimalFichaFormProps) {
  const navigate = useNavigate();
  const updateAnimal = useUpdateAnimal(animal.id);
  const { data: lots } = useLots(animal.property_id);
  const { data: breeds } = useBreeds();
  const { data: categories } = useCategories();
  const [error, setError] = useState('');
  const [sex, setSex] = useState<'male' | 'female'>(animal.sex);
  const [form, setForm] = useState(() => animalToForm(animal));
  const [isArchive, setIsArchive] = useState(animal.is_archive ?? false);

  const filteredCategories = useMemo(
    () => categories?.filter((c) => c.sex === sex) ?? [],
    [categories, sex],
  );

  function resetForm() {
    setSex(animal.sex);
    setForm(animalToForm(animal));
    setIsArchive(animal.is_archive ?? false);
    setError('');
  }

  function handleSexChange(nextSex: 'male' | 'female') {
    setSex(nextSex);
    const validCodes = new Set(
      (categories ?? []).filter((c) => c.sex === nextSex).map((c) => c.code),
    );
    if (form.category_code && !validCodes.has(form.category_code)) {
      setForm((prev) => ({ ...prev, category_code: '' }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await updateAnimal.mutateAsync({
        property_id: animal.property_id,
        internal_number: form.internal_number,
        name: form.name || null,
        sex,
        lot_id: form.lot_id ? Number(form.lot_id) : null,
        category_code: form.category_code,
        breed_id: form.breed_id ? Number(form.breed_id) : null,
        birth_date: form.birth_date || null,
        weaning_date: form.weaning_date || null,
        current_weight_kg: form.current_weight_kg ? Number(form.current_weight_kg) : null,
        chip_number: form.chip_number || null,
        registry_code: form.registry_code || null,
        notes: form.notes || null,
        is_archive: isArchive,
      });
      navigate('/animais', {
        replace: true,
        state: { message: 'Animal salvo com sucesso.' },
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao salvar.'));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-10">
        <AnimalCoverPhotoPicker
          animalId={animal.id}
          internalNumber={animal.internal_number}
          coverPhotoUrl={animal.cover_photo_url}
          sex={animal.sex}
          avatarSize="2xl"
          className="shrink-0 self-center sm:self-start"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field className="min-w-0">
              <Input
                label="Código"
                required
                value={form.internal_number}
                onChange={(e) => setForm({ ...form, internal_number: e.target.value })}
              />
            </Field>
            <Field className="min-w-0">
              <Input
                label="Nome/apelido"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field className="min-w-0">
              <Select
                label="Sexo"
                value={sex}
                onChange={(e) => handleSexChange(e.target.value as 'male' | 'female')}
                options={[
                  { value: 'female', label: 'Fêmea' },
                  { value: 'male', label: 'Macho' },
                ]}
              />
            </Field>
            <Field className="min-w-0">
              <Select
                label="Raça"
                value={form.breed_id}
                onChange={(e) => setForm({ ...form, breed_id: e.target.value })}
                placeholder="Opcional"
                options={(breeds ?? []).map((b) => ({
                  value: b.id,
                  label: breedLabel({ abbreviation: b.abbreviation, name: b.name }),
                }))}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field className="min-w-0">
              <Select
                label="Categoria"
                required
                value={form.category_code}
                onChange={(e) => setForm({ ...form, category_code: e.target.value })}
                placeholder="Selecione…"
                options={filteredCategories.map((c) => ({ value: c.code, label: c.name }))}
              />
            </Field>
            <Field className="min-w-0">
              <Select
                label="Lote"
                value={form.lot_id}
                onChange={(e) => setForm({ ...form, lot_id: e.target.value })}
                placeholder="Sem lote"
                allowEmpty
                options={(lots?.lots ?? []).map((l) => ({ value: l.id, label: l.name }))}
              />
            </Field>
            <Field className="min-w-0">
              <Input
                label="Chip"
                value={form.chip_number}
                onChange={(e) => setForm({ ...form, chip_number: e.target.value })}
              />
            </Field>
            <Field className="min-w-0">
              <Input
                label="Registro"
                value={form.registry_code}
                onChange={(e) => setForm({ ...form, registry_code: e.target.value })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field className="min-w-0">
              <WeightField
                label="Peso"
                title="Peso atual"
                value={form.current_weight_kg}
                onChange={(current_weight_kg) => setForm({ ...form, current_weight_kg })}
              />
            </Field>
            <Field className="min-w-0">
              <DateInput
                label="Nascimento"
                value={form.birth_date}
                onChange={(birth_date) => setForm({ ...form, birth_date })}
              />
            </Field>
            <Field className="min-w-0 sm:col-span-1 col-span-2">
              <DateInput
                label="Desmame"
                value={form.weaning_date}
                onChange={(weaning_date) => setForm({ ...form, weaning_date })}
              />
            </Field>
          </div>
        </div>
      </div>

      <label className="flex items-start gap-2 rounded-xl border border-manejai-sand/80 bg-manejai-cream/30 px-3 py-2.5 text-sm text-manejai-bark">
        <input
          type="checkbox"
          checked={isArchive}
          onChange={(e) => setIsArchive(e.target.checked)}
          className="mt-0.5 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-moss/30"
        />
        <span>
          <span className="font-medium text-manejai-forest">Arquivo (apenas registro genealógico)</span>
          <span className="mt-0.5 block text-xs text-manejai-bark/65">
            Animais de arquivo não aparecem em listas operacionais nem em eventos e movimentações.
          </span>
        </span>
      </label>

      <Field className="w-full">
        <Input
          label="Observações"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex flex-wrap justify-end gap-2 border-t border-manejai-sand/60 pt-3">
        <Button type="button" variant="ghost" onClick={resetForm} disabled={updateAnimal.isPending}>
          Descartar alterações
        </Button>
        <Button type="submit" loading={updateAnimal.isPending}>
          Salvar
        </Button>
      </div>
    </form>
  );
}
